import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import Button from "@mui/material/Button";
import FutureBoardProgress from "./FutureBoardProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { LIMITS, countChars } from "@chronospark/shared";

type Translate = (key: string, options?: object) => string;

interface DeleteProps {
  open: boolean;
  title: string;
  busy: boolean;
  t: Translate;
  onCancel: () => void;
  onConfirm: () => void;
}

// 完全削除の確認。対象のタイトルを示し、アーカイブとは別の操作だと伝える。
export function DeleteCardDialog({ open, title, busy, t, onCancel, onConfirm }: DeleteProps) {
  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} aria-labelledby="future-delete-title" aria-describedby="future-delete-description" maxWidth="xs" fullWidth>
      <DialogTitle id="future-delete-title">
        <Box component="span" sx={{ display: "grid", placeItems: "center", width: 48, height: 48, background: theme => theme.functions.linearGradient(theme.palette.gradients.primary.main, theme.palette.gradients.primary.state), color: "common.white", borderRadius: 3, mb: 2 }}><DeleteOutlineRounded /></Box>
        {t("futureBoard.delete.title")}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="future-delete-description" sx={{ mb: 1.5, overflowWrap: "anywhere" }}>{t("futureBoard.delete.body", { title })}</DialogContentText>
        <DialogContentText variant="body2">{t("futureBoard.delete.note")}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy} autoFocus>
          {t("futureBoard.actions.cancel")}
        </Button>
        <Button onClick={onConfirm} disabled={busy} color="error" variant="contained">
          {busy && <FutureBoardProgress size={16} color="inherit" sx={{ mr: 1 }} />}{t("futureBoard.delete.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface CreateProps {
  open: boolean;
  busy: boolean;
  t: Translate;
  onCancel: () => void;
  onCreate: (title: string) => void;
}

// 新しい未来のカードを、タイトルだけで下書きとして作る。文章や画像は、あとから描き足せる。
export function CreateCardDialog({ open, busy, t, onCancel, onCreate }: CreateProps) {
  const [title, setTitle] = useState("");
  useEffect(() => {
    if (open) setTitle("");
  }, [open]);
  const length = countChars(title.trim());
  const tooLong = length > LIMITS.title.max;
  const canCreate = length >= LIMITS.title.min && !tooLong && !busy;

  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} aria-labelledby="future-create-title" maxWidth="sm" fullWidth>
      <DialogTitle id="future-create-title">{t("futureBoard.create.title")}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          margin="dense"
          label={t("futureBoard.create.titleLabel")}
          value={title}
          onChange={event => setTitle(event.target.value)}
          onKeyDown={event => {
            if (event.key === "Enter" && !event.nativeEvent.isComposing && canCreate) {
              event.preventDefault();
              onCreate(title);
            }
          }}
          error={tooLong}
          helperText={tooLong ? t("futureBoard.validation.too_long", { max: LIMITS.title.max }) : t("futureBoard.create.titleHelp")}
          disabled={busy}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "right" }}>
          {length} / {LIMITS.title.max}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          {t("futureBoard.actions.cancel")}
        </Button>
        <Button onClick={() => onCreate(title)} disabled={!canCreate} variant="contained">
          {busy && <FutureBoardProgress size={16} color="inherit" sx={{ mr: 1 }} />}{t("futureBoard.create.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
